import { UUID } from "angular2-uuid";
import { DatePart } from "../../../view-models/utilities/date-part";
import { DispatchedServiceDto } from "../dtos/dispatched-service.dto";
import { TimePart } from "../../../view-models/utilities/time-part";
import { AccessoryServiceViewModel } from "../../../view-models/orders/accessory-service.view-model";
import { ServiceNoteViewModel } from "../../../view-models/orders/service-note.view-model";
import { PieceViewModel } from "../../../view-models/orders/piece.view-model";
import { GlobalConstants } from "../../../common/global-constants";
import { ServiceActionType } from "../service-action-type.enum";
import { DispatchedActionType } from "../dispatched-action-type.enum";
import { isValidDate } from "src/app/common/common-functions";
import { CustomerViewModel } from "src/app/view-models/orders/customer.view-model";
import { ServiceActionDto } from "../dtos/service-action.dto";
import { DocumentInformationViewModel } from "src/app/view-models/document-information.view-model";
import { ReferenceNumberViewModel } from "src/app/view-models/reference-number.view-model";
import { HazmatViewModel } from "src/app/view-models/hazmat.view-model";
import { LocationViewModel } from "src/app/view-models/location.view-model";

/**
 * Represents the view model version of a dispatched service
 */
export class DispatchedServiceViewModel {

	/**
	 * Unique Service Identifier
	 */
	public ServiceId: UUID;

	/**
	 * The identifier used as a reference for the legacy system
	 */
	public LegacyId: number;

	/**
	 * The legacy id of the order the service is a part of
	 */
	public LegacyOrderId: number;

	/**
	 * The service type (move type)
	 */
	public ServiceType: string;

	/**
	 * The reference numbers for the service
	 */
	public ReferenceNumbers: ReferenceNumberViewModel[];

	/**
	 * The accessory services related to this service
	 */
	public AccessoryServices: AccessoryServiceViewModel[];

	/**
	 * The provided number of pieces for the service
	 */
	public FlatPieces: number | null;

	/**
	 * The provided weight of the pieces for the service
	 */
	public FlatWeight: number;

	/**
	 * The provided number of skids for the service
	 */
	public FlatSkids: number | null;

	/**
	 * The displayed unit of measure for weight service
	 */
	public WeightUnitOfMeasure: string;

	/**
	 * The unit of measure for the dims.
	 */
	public DimensionsUnitOfMeasure: string;

	/**
	 * The pieces for the service.
	 */
	public Pieces: PieceViewModel[];

	/**
	 * The primary address of the service.
	 * - If the service is a pickup, the service address is the shipper.
	 * - If the service is a delivery, the service address is the consignee.
	 */
	public ServiceAddress: LocationViewModel;

	/**
	 * The secondary address associated with the service, if available.
	 * - If the service is a pickup, the secondary address (if defined) is the consignee.
	 * - If the service is a delivery, the secondary address (if defined) is the shipper.
	 */
	public SecondaryAddress: LocationViewModel | null;

	/**
	 * Indicates whether the service is special or not
	 */
	public IsSpecial: boolean;

	/**
	 * The date for the service to be rendered
	 */
	public ServiceDate: DatePart;

	/**
	 * The start of the service window
	 */
	public ServiceWindowStartTime: TimePart | null;

	/**
	 * The end of the service window
	 */
	public ServiceWindowEndTime: TimePart | null

	/**
	 * The deadline date by which the service must be completed
	 */
	public ServiceDeadlineDate: DatePart | null;

	/**
	 * The time zone of the service
	 */
	public TimeZone: string;

	/**
	 * The notes on the service
	 */
	public ServiceNotes: ServiceNoteViewModel[];

	/**
	 * The customer for the service
	 */
	public Customer: CustomerViewModel;

	/**
	 * Indicates when a service was retrieved and put on the device.
	 */
	public RetrievedDate: Date;

	/**
	 * Indicates when a service was accepted by the driver.
	 */
	public AcceptedDate: Date | null;

	/**
	 * Indicates when a service was accepted by the driver.
	 */
	public UnacceptedDate: Date | null;

	/**
	 * Indicates when a service was marked arrived by the driver.
	 */
	public ArrivedDate: Date | null;

	/**
	 * Indicates when a service could not be completed by the driver.
	 */
	public CannotCompleteDate: Date | null;

	/**
	 * Indicates when a service was marked completed by the driver.
	 */
	public CompletedDate: Date | null;

	/**
	 * Indicates when a service was marked refused by the driver.
	 */
	public RefusedDate: Date | null;

	/**
	 * Indicates when a service notes was captured by the driver.
	 */
	public NotesCompleteDate: Date | null;

	/**
	 * The timestamp for when the service has pictures taken by the driver.
	 */
	public PicturesCompleteDate: Date | null;

	/**
	 * The timestamp for when the service was OS&D'd by the driver.
	 */
	public OsdCompleteDate: Date | null;

	/**
	 * The timestamp for when the service has a signature captured by the driver.
	 */
	public SignatureCompleteDate: Date | null;

	/**
	 * The timestamp for when the service has a signer captured by the driver.
	 */
	public SignerCompleteDate: Date | null;

	/**
	 * The list of service actions.
	 */
	public ServiceActions: ServiceActionDto[];

	/**
	 * Indicates when a service notes was captured by the driver.
	 */
	public DocumentsDownloadedDate: Date | null;

	/**
	 * The information for documents for a service.
	 */
	public DocumentsInformation: DocumentInformationViewModel[] = [];

	/**
	 * The timestamp for when the service has documents completed by the driver.
	 */
	public DocumentsCompleteDate: Date | null;

	/**
	 * The timestamp for a service was previously marked cannot complete by the driver.
	 */
	public PreviousCannotCompleteDate: Date | null;

	/**
	 * Indicates whether the service was completed from end of route.
	 */
	public IsEndOfRouteComplete: boolean;

	/**
	 * Indicates if the service is visible to the driver after they click refresh.
	 */
	public IsVisibleToDriver: boolean = false;

	/**
	 * Indicates if the service is cancelled.
	 */
	public IsCancelled: boolean = false;

	/*
	* A collection of email addresses to which signed service documents are sent.
	*/
	public EmailRecipients : string[] = [];

	/**
	 * The order in which the service is displayed in the list of services for the driver.
	 */
	public Sequence: number;

	/**
	 * @constructor
	 * @param service The dispatched service from which to create the view model.
	 */
	constructor(service: DispatchedServiceDto) {
		// TODO: These should be uncommented and the defaults set above should be removed when
		// the service is mapped properly and sent in full from the server.

		this.LegacyId = service.LegacyId;
		this.LegacyOrderId = service.LegacyOrderId;
		this.ServiceId = service.ServiceId;
		this.ServiceType = service.ServiceType;
		this.ServiceAddress = service.ServiceAddress;
		this.SecondaryAddress = service.SecondaryAddress;
		this.IsSpecial = service.IsSpecial;
		this.ReferenceNumbers = service.ReferenceNumbers ?? [];
		this.AccessoryServices = service.AccessoryServices ?? [];
		this.FlatPieces = service.FlatPieces;
		this.FlatWeight = service.FlatWeight;
		this.FlatSkids = service.FlatSkids;
		this.WeightUnitOfMeasure = service.WeightUnitOfMeasure;
		this.DimensionsUnitOfMeasure = service.DimensionsUnitOfMeasure;
		this.Pieces = service.Pieces ?? [];
		this.ServiceDate = new DatePart(service.ServiceDate);
		this.Customer = service.Customer;
		this.ServiceWindowStartTime = null;
		if (service.ServiceWindowStartTime) {
			this.ServiceWindowStartTime = new TimePart(service.ServiceWindowStartTime);
		}
		this.ServiceWindowEndTime = null;
		if (service.ServiceWindowEndTime) {
			this.ServiceWindowEndTime = new TimePart(service.ServiceWindowEndTime);
		}
		this.ServiceDeadlineDate = null;
		if (service.ServiceDeadlineDate) {
			this.ServiceDeadlineDate = new DatePart(service.ServiceDeadlineDate);
		}
		this.TimeZone = service.TimeZone ?? "";
		this.ServiceNotes = service.ServiceNotes ?? [];
		this.RetrievedDate = new Date();
		this.AcceptedDate = null;
		this.UnacceptedDate = null;
		this.ArrivedDate = null;
		this.CompletedDate = null;
		this.CannotCompleteDate = null;
		this.RefusedDate = null;
		this.PicturesCompleteDate = null;
		this.OsdCompleteDate = null;
		this.SignatureCompleteDate = null;
		this.SignerCompleteDate = null;
		this.ServiceActions = service.ServiceActions ?? [];
		this.DocumentsDownloadedDate = null;
		this.DocumentsCompleteDate = null;
		this.PreviousCannotCompleteDate = null;
		this.IsEndOfRouteComplete = false;
		this.NotesCompleteDate = null;
		this.Sequence = 0;
	}

	/**
	 * Gets an empty view model for use in components as a default state.
	 * @returns An empty instance of a DispatchedServiceViewModel.
	 */
	public static GetEmptyDispatchedService(): DispatchedServiceViewModel {
		return new DispatchedServiceViewModel({
			ServiceId: GlobalConstants.EMPTY_GUID,
			LegacyId: 0,
			LegacyOrderId: 0,
			ServiceType: '',
			ReferenceNumbers: [],
			AccessoryServices: [],
			FlatPieces: null,
			FlatWeight: 0,
			FlatSkids: null,
			WeightUnitOfMeasure: "",
			DimensionsUnitOfMeasure: "",
			Pieces: [],
			ServiceAddress: {
				AddressName: null,
				Address1: null,
				Address2: null,
				City: null,
				Region: null,
				PostalCode: null,
				AirportCode: null,
				ContactName: null,
				PhoneNumber: null,
				Instructions: null
			},
			SecondaryAddress: null,
			IsSpecial: false,
			ServiceDate: "1970-01-01",
			ServiceWindowStartTime: null,
			ServiceWindowEndTime: null,
			ServiceDeadlineDate: null,
			TimeZone: "",
			ServiceNotes: [],
			Customer: {
				Name: "",
				Terminal: ""
			},
			ServiceActions: [],
			DocumentsInformation: [],
			Type: DispatchedActionType.Service
		});
	}

	/**
	 * Indicates if the service has been accepted.
	 * @returns True if there is an accepted date.
	 * @readonly
	 */
	public get HasDriverAccepted(): boolean {
		return isValidDate(this.AcceptedDate);
	}

	/**
	 * Indicates if the service is marked arrived.
	 * @returns True if the service is marked arrived.
	 * @readonly
	 */
	public get HasDriverArrived(): boolean {
		return isValidDate(this.ArrivedDate);
	}

	/**
	 * Indicates if the service could not be completed.
	 * @returns True if there is a cannot complete date.
	 * @readonly
	 */
	public get HasDriverCouldNotComplete(): boolean {
		return isValidDate(this.CannotCompleteDate);
	}

	/**
	 * Indicates if the service is marked refused.
	 * @returns True if there is a refused date.
	 * @readonly
	 */
	public get HasDriverRefused(): boolean {
		return isValidDate(this.RefusedDate);
	}

	/**
	 * Indicates if the service is marked completed.
	 * @returns True if there is a complete date.
	 * @readonly
	 */
	public get HasDriverCompleted(): boolean {
		return isValidDate(this.CompletedDate);
	}

	/**
	 * Indicates if the service's documents have been downloaded to the mobile device.
	 * @returns True if there is a downloaded date.
	 * @readonly
	 */
	public get HaveDocumentsBeenDownloaded(): boolean {
		return isValidDate(this.DocumentsDownloadedDate);
	}

	/**
	 * Indicates is the service is visible to the driver and not completed or refused.
	 * @returns True if the service is visible to the driver and not completed or refused.
	 * @readonly
	 */
	public get IsActive(): boolean {
		return this.IsVisibleToDriver && !this.HasDriverCompleted && !this.HasDriverRefused;
	}

	/**
	 * Indicates if the service has been accepted and not performed.
	 * @returns True if there is an accepted date, but not a could not complete date, a completed date, or a refused date.
	 * @readonly
	 */
	public get IsAcceptedAndNotPerformed(): boolean {
		return this.HasDriverAccepted && !this.HasBeenPerformed;
	}

	/**
	 * Indicates if the service has been accepted and performed.
	 * @returns True if there is an accepted date, and the service is DriverCompleted, DriverCouldNotComplete, or DriverRefused.
	 * @readonly
	 */
	public get IsAcceptedAndPerformed(): boolean {
		return this.HasDriverAccepted && this.HasBeenPerformed;
	}

	/**
	 * Indicates if the service has already been performed by comleting, couldn't completing, or refusing the service.
	 * @returns True if the service has already been performed and false if it has not.
	 * @readonly
	 */
	public get HasBeenPerformed(): boolean {
		return this.HasDriverCouldNotComplete || this.HasDriverCompleted || this.HasDriverRefused;
	}

	/**
	 * Indicates if the service has been OS&D'd.
	 * @returns True if there is a OS&D Date.
	 * @readonly
	 */
	public get DriverOsdComplete(): boolean {
		return isValidDate(this.GetServiceAction(ServiceActionType.OSD)?.CompletedDate || null);
	}

	/**
	 * Gets the window start time as a string
	 * @returns The start time as a string
	 * @readonly
	 */
	public get StartTimeText(): string {
		if (!this.ServiceWindowStartTime)
			return "";
		return this.ServiceWindowStartTime.ToStringNoSeconds();
	}

	/**
	 * Gets the window end time as a string
	 * @returns The end time as a string
	 * @readonly
	 */
	public get EndTimeText(): string {
		if (!this.ServiceWindowEndTime)
			return "";
		return this.ServiceWindowEndTime.ToStringNoSeconds();
	}

	/**
	 * Gets a collection of internal reference numbers.
	 * @returns The service's reference numbers that are not considered external.
	 * @readonly
	 */
	public get InternalReferenceNumbers(): ReferenceNumberViewModel[] {
		return this.ReferenceNumbers.filter((refNum: ReferenceNumberViewModel) => GlobalConstants.INTERNAL_REFERENCE_NUMBER_TYPES.filter((internalType: string) => internalType == refNum.Type).length > 0);
	}

	/**
	 * Gets a collection of external reference numbers.
	 * @returns The service's reference numbers that are considered external.
	 * @readonly
	 */
	public get ExternalReferenceNumbers(): string[] {
		return this.ReferenceNumbers.filter((refNum: ReferenceNumberViewModel) => GlobalConstants.INTERNAL_REFERENCE_NUMBER_TYPES.filter((internalType: string) => internalType == refNum.Type).length == 0)
			.map((refNum: ReferenceNumberViewModel) => refNum.Number);
	}

	/**
	 * Checks if the required service actions are completed.
	 * @returns True if all required service actions are completed or does not have required service actions at all.
	 * @readonly
	 */
	public get AreRequiredActionsIncomplete(): boolean {
		return this.ServiceActions.some((action: ServiceActionDto) => action.Required && !action.CompletedDate);
	}

	/**
	 * Gets the OS&D service action.
	 * @readonly
	 * @returns The OS&D service action.
	 */
	public get OsdServiceAction(): ServiceActionDto | null {
		return this.GetServiceAction(ServiceActionType.OSD);
	}

	/**
	 * Gets the Signature service action.
	 * @readonly
	 * @returns The Signature service action.
	 */
	public get PictureServiceAction(): ServiceActionDto | null {
		return this.GetServiceAction(ServiceActionType.Pictures);
	}

	/**
	 * Gets the Documents(eDocs) service action.
	 * @readonly
	 * @returns The Documents(eDocs) service action.
	 */
	public get DocsServiceAction(): ServiceActionDto | null {
		return this.GetServiceAction(ServiceActionType.Documents);
	}

	/**
	 * Gets the Signature service action.
	 * @readonly
	 * @returns The Signature service action.
	 */
	public get SignatureServiceAction(): ServiceActionDto | null {
		return this.GetServiceAction(ServiceActionType.Signature);
	}

	/**
	 * Gets the Signer service action.
	 * @readonly
	 * @returns The Signer service action.
	 */
	public get SignerServiceAction(): ServiceActionDto | null {
		return this.GetServiceAction(ServiceActionType.Signer);
	}

	/**
	 * Gets the Hazmat info for the whole service
	 * @readonly
	 * @returns The Hazmat info for the whole service if all pieces have the same hazmat information, null otherwise.
	 */
	public get HazmatAtService(): HazmatViewModel | null {
		if (this.Pieces.length == 0)
			return null;
		return this.Pieces.every((piece: PieceViewModel) => {
			return piece.Hazmat
			&& this.Pieces[0].Hazmat!.ClassNumber == piece.Hazmat!.ClassNumber
			&& this.Pieces[0].Hazmat!.UNNumber == piece.Hazmat!.UNNumber
			&& this.Pieces[0].Hazmat!.PackageGroup == piece.Hazmat!.PackageGroup
		}) ? this.Pieces[0].Hazmat : null;
	}

	/**
	 * Gets a booleans indicating if any of a service's pieces are hazmat
	 * @readonly
	 * @returns A boolean indicating if any of a service's pieces are hazmat
	 */
	public get IsServiceHazmat(): boolean {
		return this.Pieces.some((piece: PieceViewModel) => { return piece.Hazmat});
	}

	/**
	 * Gets the dims and description for the piece to display.
	 * @param piece The piece from which to get the dims
	 * @returns The display version of the dims and description for the piece.
	 */
	public GetPieceDimsText(piece: PieceViewModel): string {
		return `${piece.Dimensions.Length}${this.DimensionsUnitOfMeasure} X ${piece.Dimensions.Width}${this.DimensionsUnitOfMeasure} X ${piece.Dimensions.Height}${this.DimensionsUnitOfMeasure}`
	}

	/**
	 * Gets the weight and UoM for a piece.
	 * @param piece The piece with a weight.
	 * @returns The piece weight in the format of XXu.
	 */
	public GetPieceWeightText(piece: PieceViewModel): string {
		return `${piece.Dimensions.Weight} ${this.WeightUnitOfMeasure}`;
	}

	/**
	 * Gets the service action for a given type if it exists.
	 * @param actionType The action type to find.
	 * @returns The service action if it exists.
	 */
	public GetServiceAction(actionType: ServiceActionType): ServiceActionDto | null {
		return this.ServiceActions.filter((action: ServiceActionDto) => action.ActionType == actionType)[0] || null;
	}
}